import axios from "axios";
import moment from "moment";
import { appInsights } from "shared/utils/helperFunctions/appInsights"; 

import configureStore from "redux/configureStore";

const { persistor } = configureStore();

// Several requests can be in flight at once (e.g. right after login), and each
// would independently see the mismatched header and trigger its own
// pause/flush/purge/reload. This guard ensures only the first one acts.
let isReloadingForVersionMismatch = false;

const subscriptionKey =
  window.jllConfig && window.jllConfig.subscription_token
    ? window.jllConfig.subscription_token
    : process.env.REACT_APP_SUBSCRIPTION_KEY;

const getAccessCheck = () => {
  var accessCheck = window.accessCheck;

  if (!accessCheck) {
    const token = JSON.parse(localStorage.getItem("okta-token-storage"))
      .accessToken?.accessToken;

    accessCheck = {
      auth: {
        getAccessToken: () => {
          return token;
        },
      },
    };
  }

  return accessCheck;
};

export function deleteRequest(url, data) {
  const accessCheck = getAccessCheck();

  return callApi(accessCheck, false, url, data, "DELETE");
}

export function postRequest(
  url,
  data,
  ignoreResponse = false,
  responseType,
  onUploadProgress
) {
  const accessCheck = getAccessCheck();

  return callApi(
    accessCheck,
    false,
    url,
    data,
    "POST",
    responseType,
    ignoreResponse,
    onUploadProgress
  );
}

export function putRequest(url, data, onUploadProgress) {
  const accessCheck = getAccessCheck();

  return callApi(
    accessCheck,
    false,
    url,
    data,
    "PUT",
    undefined,
    false,
    onUploadProgress
  );
}

export function getRequest(url, responseType) {
  const accessCheck = getAccessCheck();

  return callApi(accessCheck, true, url, null, null, responseType);
}

async function callApi(
  accessCheck,
  isGet,
  apiUrl,
  apiData,
  methodType,
  responseType,
  ignoreResponse = false,
  onUploadProgress
) {
  let method = "post";
  let data = {};

  if (apiData) {
    data = apiData;
  }

  if (isGet) {
    method = "get";
    data = "";
  }

  if (methodType) {
    method = methodType;
  }

  if (accessCheck?.auth) {
    if (checkAuthentication()) {
      const authenticated = await renewSession(accessCheck.auth);

      if (authenticated) {
        return callRespectiveFunction(
          accessCheck,
          method,
          data,
          apiUrl,
          responseType,
          ignoreResponse,
          onUploadProgress
        );
      } else {
        accessCheck.history?.push("/"); // ?? window.location.href = `${window.location.host}`;
        // accessCheck.history.push("/sessionexpired");
      }
    } else {
      return callRespectiveFunction(
        accessCheck,
        method,
        data,
        apiUrl,
        responseType,
        ignoreResponse,
        onUploadProgress
      );
    }
  }
}

function callRespectiveFunction(
  accessCheck,
  method,
  data,
  url,
  responseType,
  ignoreResponse,
  onUploadProgress,
  retryCount = 0
) {
  const token = accessCheck.auth.getAccessToken();

  if (token) {
    return axios({
      method: method,
      url: url,
      crossdomain: true,
      timeout: 600000,
      headers: {
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
        Authorization: `Bearer ${token}`,
        "Subscription-Key": subscriptionKey,
      },
      data: data,
      responseType: responseType !== undefined ? responseType : "json",
      ...(onUploadProgress ? { onUploadProgress } : {}),
    })
      .then((response) => {
        if (
          response?.headers["x-ui-version"] &&
          response.headers["x-ui-version"] !== localStorage.getItem("ui-version") &&
          !isReloadingForVersionMismatch
        ) {
          isReloadingForVersionMismatch = true;

          console.log("UI Version Mismatch - Reloading");

          localStorage.setItem("ui-version", response.headers["x-ui-version"]);

          persistor.pause();
          persistor.flush().then(() => {
            return persistor.purge();
          }).finally(() => {
            window.location.reload();
          });
        }

        if (
          response.status !== 200 &&
          response.status !== 204 &&
          !ignoreResponse
        ) {
          if (response.status === 401 || response.status === 403) {
            return handleUnauthorizedResponse(
              accessCheck,
              method,
              data,
              url,
              responseType,
              ignoreResponse,
              onUploadProgress,
              retryCount,
              response.status
            );
          } else {
            return response;
          }
        } else if (response) {
          return response;
        }
      })
      .catch((ex) => {
        const { response } = ex;
        if (!ignoreResponse) {
          if (
            response &&
            (response.status === 401 || response.status === 403)
          ) {
            return handleUnauthorizedResponse(
              accessCheck,
              method,
              data,
              url,
              responseType,
              ignoreResponse,
              onUploadProgress,
              retryCount,
              response.status,
              ex
            );
          } else {

            //accessCheck.history.push(`/error?message=${ex.message}`);
            const exceptionDetails = { error: ex, data };
            appInsights.trackException(exceptionDetails);
          }
        } else {
          console.log("User Analytics Error - " + response);
        }
      });
  } else {
    //accessCheck.history.push(`/error?message='Token Expired'`);
  }
}

// A 401/403 doesn't always mean this account genuinely lacks access - it can also
// mean the gateway rejected a token that still looked valid to our own client-side
// expiry check (e.g. clock skew, a just-rotated signing key, a momentary gateway
// hiccup). Previously, ANY 401/403 from ANY call immediately booted the whole app
// to /unauthorized with no way to recover from a bad-but-not-really-expired token.
// Now, the first time this happens for a given call, we renew the Okta token once
// and replay the exact same request before giving up. Only a second failure (or a
// failed renewal) actually sends the user to /unauthorized.
async function handleUnauthorizedResponse(
  accessCheck,
  method,
  data,
  url,
  responseType,
  ignoreResponse,
  onUploadProgress,
  retryCount,
  status,
  ex
) {
  if (retryCount === 0 && accessCheck?.auth?.tokenManager) {
    const renewed = await renewSession(accessCheck.auth);

    if (renewed) {
      return callRespectiveFunction(
        accessCheck,
        method,
        data,
        url,
        responseType,
        ignoreResponse,
        onUploadProgress,
        retryCount + 1
      );
    }
  }

  accessCheck.history.push(`/unauthorized`, { status });

  if (ex) {
    appInsights.trackException({ error: ex, response: ex.response });
  }

  return ex?.response;
}

async function renewSession(auth) {
  let promise = auth.tokenManager
    .renew("accessToken")
    .then(function (freshToken) {
      let oldtoken = JSON.parse(localStorage.getItem("okta-token-storage"));
      oldtoken.accessToken = freshToken;
      localStorage.setItem("okta-token-storage", JSON.stringify(oldtoken));
      localStorage.setItem("oktaAccessToken", freshToken);

      return true;
    })
    .catch(function (err) {
      localStorage.removeItem("okta-cache-storage");
      localStorage.removeItem("okta-token-storage");
      return false;
    });

  let result = await promise;
  return result;
}

const checkAuthentication = () => {
  const tokenToRenew = JSON.parse(
    localStorage.getItem("okta-token-storage")
  ).accessToken;

  if (
    tokenToRenew &&
    tokenToRenew.expiresAt &&
    moment(moment.utc(moment.unix(tokenToRenew.expiresAt).format())).isBefore(
      moment.utc(moment(new Date()).add(30, "minutes").format())
    )
  ) {
    return true;
  } else {
    return false;
  }
};
